import { LoadError } from '@/components/LoadError';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { goalHorizons, type GoalHorizon } from '@horizon/shared';
import { EmptyState } from '@/components/EmptyState';
import { GoalCard } from '@/components/GoalCard';
import { Button } from '@/components/ui/button';
import { useCreateGoal, useGoals } from '@/lib/queries';

export default function GoalsPage() {
  const { t } = useTranslation();
  const goals = useGoals();
  const createGoal = useCreateGoal();
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [horizon, setHorizon] = useState<GoalHorizon>('quarter');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) return;
    setError(false);
    try {
      await createGoal.mutateAsync({ title: title.trim(), horizon });
      setTitle('');
      setOpen(false);
    } catch {
      setError(true);
    }
  };

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-2xl font-semibold">{t('goals.title')}</h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {t('goals.new')}
          </Button>
          <Link
            to="/archive"
            className="inline-flex min-h-11 min-w-11 items-center rounded-lg px-3 text-sm text-muted hover:text-ink"
          >
            {t('archive.title')}
          </Link>
        </div>
      </div>

      {open && (
        <form
          onSubmit={submit}
          className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4"
        >
          <label className="flex flex-col gap-1 text-sm">
            {t('goal.field.title')}
            <input
              disabled={createGoal.isPending}
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="min-h-11 rounded-lg border border-line bg-bg px-3 outline-none focus:border-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            {t('goal.field.horizon')}
            <select
              disabled={createGoal.isPending}
              value={horizon}
              onChange={(e) => setHorizon(e.target.value as GoalHorizon)}
              className="min-h-11 rounded-lg border border-line bg-bg px-3 outline-none focus:border-accent"
            >
              {goalHorizons.map((value) => (
                <option key={value} value={value}>
                  {t(`goal.horizon.${value}`)}
                </option>
              ))}
            </select>
          </label>
          {error && <p role="alert">{t('polish.draftError')}</p>}
          <div className="flex gap-2">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!title.trim() || createGoal.isPending}
            >
              {t('common.create')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}

      {goals.isError && <LoadError retry={() => goals.refetch()} />}

      {goals.data && goals.data.length > 0 ? (
        <ul className="grid gap-3 md:grid-cols-2">
          {goals.data.map((goal) => (
            <GoalCard key={goal.id} goal={goal} />
          ))}
        </ul>
      ) : (
        goals.isSuccess && <EmptyState titleKey="goals.empty.title" hintKey="goals.empty.hint" />
      )}
    </section>
  );
}
